import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, Upload, message, Select } from 'antd';
import { PuffLoader } from 'react-spinners';
import axios from 'axios';
import './careerdetail.scss';
import { InboxOutlined, EnvironmentOutlined, ClockCircleOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import BASE_URL from '../../Util/config';
import { toast } from 'react-toastify';

const { TextArea } = Input;
const { Dragger } = Upload;

const CareerDetailEn = ({ selectedLoker, open, setOpen }) => {
  const [careerDetail, setCareerDetail] = useState(null);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [captchaToken, setCaptchaToken] = useState('');
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  const educationOptions = [
    { value: 'elementary', label: 'Elementary School' },
    { value: 'junior_high', label: 'Junior High School' },
    { value: 'senior_high', label: 'Senior High School' },
    { value: 'vocational', label: 'Vocational School' },
    { value: 'diploma', label: 'Diploma (Associate Degree)' },
    { value: 'bachelor', label: "Bachelor's Degree" },
    { value: 'master', label: "Master's Degree" },
    { value: 'doctorate', label: 'Doctorate (Ph.D.)' },
  ];

  // Load Cloudflare Turnstile script
  useEffect(() => {
    if (!document.getElementById('turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  useEffect(() => {
    if (open && selectedLoker) {
      setLoading(true);

      axios
        .get(`${BASE_URL}/api/careers-cms/${selectedLoker}`)
        .then((res) => {
          const detail = res.data.data || res.data;

          if (detail.id !== selectedLoker) {
            console.warn('Mismatch ID! Expected:', selectedLoker, 'Got:', detail.id);
          }

          setCareerDetail(detail);
          setTitle(detail.name_en || detail.name || 'Position Not Available');
          setLoading(false);
          setShowForm(false);
        })
        .catch((err) => {
          console.error('Error fetching career detail:', err);
          message.error('Failed to load job details');
          setLoading(false);
        });
    }

    return () => {
      // Cleanup Turnstile widget
      if (!open && turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
          turnstileWidgetId.current = null;
        } catch (error) {
          console.log('Turnstile cleanup error:', error);
        }
      }
    };
  }, [open, selectedLoker]);

  // Initialize Turnstile when form is shown
  useEffect(() => {
    if (showForm && turnstileRef.current && window.turnstile) {
      // Remove existing widget if any
      if (turnstileWidgetId.current) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (error) {
          console.log('Turnstile remove error:', error);
        }
      }

      // Render new widget
      try {
        turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
          // Testing keys - always passes (for development)
          // sitekey: '1x00000000000000000000AA', // Always passes
          // sitekey: '2x00000000000000000000AB', // Always blocks
          // sitekey: '3x00000000000000000000FF', // Force interactive challenge

          // Production key - replace with your VALID site key
          sitekey: '0x4AAAAAACLdEg1n1jVbZAjq',

          theme: 'light', // or 'dark'
          size: 'normal', // or 'compact'
          callback: (token) => {
            setCaptchaToken(token);
            console.log('Turnstile verified successfully');
          },
          'error-callback': (errorCode) => {
            setCaptchaToken('');
            console.error('Turnstile error:', errorCode);
            toast.error('Verification failed. Please try again.', {
              position: 'top-right',
              autoClose: 3000,
            });
          },
          'expired-callback': () => {
            setCaptchaToken('');
            console.log('Turnstile token expired');
          },
          'timeout-callback': () => {
            setCaptchaToken('');
            console.log('Turnstile timeout');
            toast.error('Verification timeout. Please try again.', {
              position: 'top-right',
              autoClose: 3000,
            });
          },
        });
      } catch (error) {
        console.error('Turnstile render error:', error);
      }
    }
  }, [showForm]);

  const resetForm = () => {
    setShowForm(false);
    setFileList([]);
    setCaptchaToken('');

    // Reset Turnstile
    if (turnstileWidgetId.current && window.turnstile) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.log('Turnstile reset error:', error);
      }
    }

    form.resetFields();
  };

  const handleApplyClick = () => {
    setShowForm(true);
  };

  const handleCancelClick = () => {
    resetForm();
  };

  const onFinish = async (values) => {
    if (!captchaToken) {
      toast.error('Please complete the security verification first', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    if (fileList.length === 0) {
      toast.error('CV/Resume is required', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('fullName', values.fullName);
      formDataToSend.append('email', values.email);
      formDataToSend.append('phone', values.phone);
      formDataToSend.append('address', values.address);
      formDataToSend.append('birthDate', values.birthDate);
      formDataToSend.append('education', values.education);
      formDataToSend.append('experience', values.experience || '');
      formDataToSend.append('coverLetter', values.coverLetter);
      formDataToSend.append('portfolio', values.portfolio || '');

      if (fileList[0]?.originFileObj) {
        formDataToSend.append('resume', fileList[0].originFileObj);
      }

      const response = await axios.post(`${BASE_URL}/api/careers/${selectedLoker}/apply`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const result = response.data;

      if (result.success || response.status === 200) {
        toast.success('Your application has been submitted successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });

        // Reset Turnstile
        if (turnstileWidgetId.current && window.turnstile) {
          try {
            window.turnstile.reset(turnstileWidgetId.current);
          } catch (error) {
            console.log('Turnstile reset error:', error);
          }
        }

        setTimeout(() => {
          setOpen(false);
          resetForm();
        }, 800);
      } else {
        toast.error(result.message || 'An error occurred while submitting your application', {
          position: 'top-right',
          autoClose: 4000,
        });

        // Reset captcha on error
        if (turnstileWidgetId.current && window.turnstile) {
          try {
            window.turnstile.reset(turnstileWidgetId.current);
          } catch (error) {
            console.log('Turnstile reset error:', error);
          }
        }
        setCaptchaToken('');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error.response?.data?.message || 'An error occurred while submitting your application', {
        position: 'top-right',
        autoClose: 4000,
      });

      // Reset captcha on error
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.reset(turnstileWidgetId.current);
        } catch (error) {
          console.log('Turnstile reset error:', error);
        }
      }
      setCaptchaToken('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadProps = {
    fileList,
    multiple: false,
    maxCount: 1,
    accept: '.pdf,.doc,.docx',
    beforeUpload: (file) => {
      const isPdfOrDocx = file.type === 'application/pdf' || file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.type === 'application/msword';

      if (!isPdfOrDocx) {
        message.error('You can only upload PDF or DOCX file!');
        return Upload.LIST_IGNORE;
      }

      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File size must not exceed 5MB!');
        return Upload.LIST_IGNORE;
      }

      setFileList([file]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
    onChange: (info) => {
      setFileList(info.fileList);
    },
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      resetForm();
    }
  };

  const getJobTypeLabel = (type) => {
    const types = {
      full_time: 'Full Time',
      part_time: 'Part Time',
      contract: 'Contract',
      internship: 'Internship',
    };
    return types[type] || type || '-';
  };

  return (
    <Modal className="modern-career-modal" centered open={open} onCancel={handleModalClose} footer={null} width={1000} destroyOnClose maskClosable={!isSubmitting} keyboard={!isSubmitting}>
      {loading ? (
        <div className="loading-spinner">
          <PuffLoader color="#1a94ff" size={80} />
        </div>
      ) : (
        <>
          {!showForm ? (
            <div className="career-detail-view">
              {/* Header Section */}
              <div className="career-header">
                <h1 className="career-title">{title}</h1>

                {/* Quick Info Tags */}
                <div className="quick-info">
                  {careerDetail?.location && (
                    <div className="info-tag">
                      <EnvironmentOutlined />
                      <span>{careerDetail.location}</span>
                    </div>
                  )}
                  {careerDetail?.type && (
                    <div className="info-tag">
                      <ClockCircleOutlined />
                      <span>{getJobTypeLabel(careerDetail.type)}</span>
                    </div>
                  )}
                  {careerDetail?.totalNeeded && (
                    <div className="info-tag">
                      <TeamOutlined />
                      <span>{careerDetail.totalNeeded} positions</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Content Section */}
              <div className="career-content-detail">
                {/* Description */}
                {careerDetail?.description_en && (
                  <div className="content-section">
                    <h3>Job Description</h3>
                    <p className="description-text">{careerDetail.description_en}</p>
                  </div>
                )}

                {/* Requirements */}
                {careerDetail?.requirements_en && careerDetail.requirements_en.length > 0 && (
                  <div className="content-section">
                    <h3>Requirements</h3>
                    <ul className="styled-list">
                      {careerDetail.requirements_en.map((req, idx) => (
                        <li key={idx}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Responsibilities */}
                {careerDetail?.responsibilities_en && careerDetail.responsibilities_en.length > 0 && (
                  <div className="content-section">
                    <h3>Responsibilities</h3>
                    <ul className="styled-list">
                      {careerDetail.responsibilities_en.map((resp, idx) => (
                        <li key={idx}>{resp}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Additional Info Grid */}
                <div className="info-grid">
                  {careerDetail?.education && (
                    <div className="info-card">
                      <div className="info-label">Education</div>
                      <div className="info-value">{careerDetail.education}</div>
                    </div>
                  )}
                  {careerDetail?.experience && (
                    <div className="info-card">
                      <div className="info-label">Experience</div>
                      <div className="info-value">{careerDetail.experience}</div>
                    </div>
                  )}
                  {careerDetail?.deadline && (
                    <div className="info-card deadline">
                      <div className="info-label">
                        <CalendarOutlined /> Application Deadline
                      </div>
                      <div className="info-value">
                        {new Date(careerDetail.deadline).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Apply Button */}
              <div className="apply-section">
                <button className="btn-apply-modern" onClick={handleApplyClick}>
                  <span>Apply for This Job</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div className="application-form-view">
              {/* Form Header */}
              <div className="form-header">
                <h1 className="form-title">Complete Your Information</h1>
                <p className="form-subtitle">
                  Applying for position: <strong>{title}</strong>
                </p>
              </div>

              {/* Form */}
              <Form form={form} onFinish={onFinish} layout="vertical" className="modern-form" preserve={false}>
                <div className="form-grid">
                  <Form.Item label="Full Name" name="fullName" rules={[{ required: true, message: 'Full name is required' }]}>
                    <Input placeholder="Enter your full name" />
                  </Form.Item>

                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Email is required' },
                      { type: 'email', message: 'Invalid email format' },
                    ]}
                  >
                    <Input placeholder="example@email.com" />
                  </Form.Item>
                </div>

                <div className="form-grid">
                  <Form.Item label="Phone Number" name="phone" rules={[{ required: true, message: 'Phone number is required' }]}>
                    <Input placeholder="+62xxxxxxxxxx" />
                  </Form.Item>

                  <Form.Item label="Date of Birth" name="birthDate" rules={[{ required: true, message: 'Date of birth is required' }]}>
                    <Input type="date" />
                  </Form.Item>
                </div>

                <Form.Item label="Full Address" name="address" rules={[{ required: true, message: 'Address is required' }]}>
                  <TextArea rows={3} placeholder="Enter your complete address..." />
                </Form.Item>

                <Form.Item label="Highest Education" name="education" rules={[{ required: true, message: 'Education is required' }]}>
                  <Select placeholder="Select your highest education" allowClear>
                    {educationOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item label="Work Experience" name="experience">
                  <TextArea rows={4} placeholder="Tell us about your work experience (optional)..." />
                </Form.Item>

                <Form.Item label="Portfolio/Website" name="portfolio">
                  <Input placeholder="https://portfolio.com (optional)" />
                </Form.Item>

                <Form.Item label="CV / Resume" name="resume" rules={[{ required: true, message: 'CV/Resume is required' }]}>
                  <Dragger {...uploadProps} className="modern-upload">
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Click or drag file to this area</p>
                    <p className="ant-upload-hint">Format: PDF or DOCX (Max. 5MB)</p>
                  </Dragger>
                </Form.Item>

                <Form.Item label="Cover Letter" name="coverLetter" rules={[{ required: true, message: 'Cover letter is required' }]}>
                  <TextArea rows={6} placeholder="Tell us about your motivation, skills, and why you're applying for this position..." maxLength={1000} showCount />
                </Form.Item>

                <Form.Item name="captcha">
                  <div className="captcha-container">
                    <div ref={turnstileRef}></div>
                  </div>
                </Form.Item>

                <div className="form-actions">
                  <button className="btn-cancel" type="button" onClick={handleCancelClick} disabled={isSubmitting}>
                    Cancel
                  </button>
                  <button className="btn-submit" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </Form>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default CareerDetailEn;
